import { HintLabel } from '@skupreme/shared/ui';
import { DateUtility } from '@utility/date';
import type { ReactNode } from 'react';
import { type InventoryItem, stockOutlook } from '../../lib/inventory';

const humanise = (days: number) =>
  DateUtility.yearMonthTime(new Date(), DateUtility.addDays(new Date(), days));

function Line({ children }: { children: ReactNode }) {
  return <span className="sk:whitespace-nowrap">{children}</span>;
}

function Units({ children }: { children: ReactNode }) {
  return (
    <>
      {children} <span className="sk:opacity-70">Units</span>
    </>
  );
}

/** How the days of inventory are worked out from the sales velocity. */
function VelocityBreakdown({
  item,
  isFBA,
  daysLeft,
}: {
  item: InventoryItem;
  isFBA: boolean;
  daysLeft: ReactNode;
}) {
  const reserved =
    (isFBA ? item.reservedWarehouseTransferQuantity : item.reservedQuantity) ||
    0;
  const available = item.available ?? 0;

  return (
    <span className="sk:flex sk:flex-col sk:gap-0.5">
      <Line>
        Sales Velocity = {item.sales_velocity}{' '}
        <span className="sk:opacity-70">Units / Day</span>
      </Line>
      <Line>
        Quantity in Hand = <Units>{available}</Units>
      </Line>
      {reserved ? (
        <>
          <Line>
            Reserved Quantity = <Units>{reserved}</Units>
          </Line>
          <Line>
            Total Quantity = <Units>{available}</Units> +{' '}
            <Units>{reserved}</Units> = <Units>{available + reserved}</Units>
          </Line>
        </>
      ) : null}
      <Line>
        Days of Inventory = <Units>{available + reserved}</Units> /{' '}
        {item.sales_velocity} = {daysLeft}
      </Line>
    </span>
  );
}

/** "(12 Days Left)", "(Out of Stock)" and the like, under a quantity. */
export function StockOutlookText({
  item,
  onHand,
  isProcessing,
  isFBA,
}: {
  item: InventoryItem;
  onHand: number;
  isProcessing: boolean;
  isFBA: boolean;
}) {
  const outlook = stockOutlook(item, onHand, isProcessing, humanise);

  switch (outlook.kind) {
    case 'processing':
      return <span>(Processing)</span>;
    case 'out':
      return <span>(Out of Stock)</span>;
    case 'no-velocity':
      return <span>(No Sales Velocity)</span>;
    default: {
      const daysLeft =
        outlook.kind === 'days' ? `${outlook.days} Days` : outlook.span;

      return (
        <HintLabel
          hint={
            <VelocityBreakdown item={item} isFBA={isFBA} daysLeft={daysLeft} />
          }
          className="sk:whitespace-nowrap"
        >
          ({daysLeft} Left)
        </HintLabel>
      );
    }
  }
}
